import { posthog } from "posthog-js";
import type { ComponentType } from "preact";
import { useLocation } from "preact-iso";
import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  Button,
  CtaButton,
  inputClass,
  Logo,
  PageSpinner,
} from "./components.tsx";
import { loginPath, switchAccountParam } from "../../constants.ts";
import { auth, useAuth } from "./db.ts";
import { usePageMeta } from "./pageMeta.ts";
import { builderChatPath } from "./paths.ts";
import { fireAnalyticsEventLegacy } from "./utils.ts";

type VoidProps = Record<string, never>;

export const resolveRedirectUrl = (
  redirectTo: string | null | undefined,
  refreshToken?: string,
): string | null => {
  if (!redirectTo) return null;
  const base = typeof globalThis.location !== "undefined"
    ? globalThis.location.origin
    : "https://prompt2bot.com";
  const redirectUrl = new URL(redirectTo, base);
  if (refreshToken) {
    redirectUrl.searchParams.set("token", refreshToken);
  }
  return redirectUrl.toString();
};

const MagicCode = (
  { sentEmail, onSuccess, onCancel }: {
    sentEmail: string;
    onSuccess?: () => void;
    onCancel?: () => void;
  },
) => {
  const [code, setCode] = useState("");
  const { route } = useLocation();
  return (
    <div>
      <h2 className="text-text-primary-light dark:text-text-primary font-body">
        Okay, we sent you an email! What was the code?
      </h2>
      <h3 className="text-text-secondary-light dark:text-text-secondary text-sm mt-1">
        if you don't see it, check your spam folder.
      </h3>
      <div className="mt-3">
        <input
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="123456..."
          value={code}
          onInput={(e) => setCode(e.currentTarget.value)}
          className={inputClass}
        />
      </div>
      <div className="flex gap-2 mt-4">
        <Button
          onClick={() => {
            auth.signInWithMagicCode({ email: sentEmail, code })
              .then((res: { user?: { refresh_token?: string } }) => {
                toast.success("Logged in!");
                if (onSuccess) {
                  onSuccess();
                  return;
                }
                const params = new URLSearchParams(globalThis.location?.search);
                const redirectUrl = resolveRedirectUrl(
                  params.get("redirect_to"),
                  res?.user?.refresh_token,
                );
                if (redirectUrl) {
                  globalThis.location.href = redirectUrl;
                } else {
                  route(builderChatPath);
                }
              })
              .catch(
                (err: { body?: { message: string } }) => {
                  toast.error(`Uh oh :${err.body?.message}`);
                  setCode("");
                },
              );
          }}
          type="button"
          className="flex-1"
        >
          Verify
        </Button>
        {onCancel && (
          <Button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
          >
            Back
          </Button>
        )}
      </div>
    </div>
  );
};

const authButtonBase =
  "mt-2 px-6 py-3 rounded-lg font-medium text-center w-full max-w-none self-stretch flex items-center justify-center gap-2 text-base shadow-sm transition-all duration-200 cursor-pointer font-body";

const LoginWithEmail = (
  { setSentEmail, onCancel }: {
    setSentEmail: (email: string) => void;
    onCancel: () => void;
  },
) => {
  const [email, setEmail] = useState("");
  return (
    <div>
      <div className="mt-3">
        <input
          placeholder="Enter your email"
          type="email"
          value={email}
          onInput={(e) => setEmail(e.currentTarget.value)}
          className={inputClass}
        />
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          type="button"
          onClick={() => {
            if (!email) return;
            setSentEmail(email);
            auth.sendMagicCode({ email }).catch(
              (err: { body?: { message: string } }) => {
                toast.error(`Uh oh :${err.body?.message}`);
                setSentEmail("");
              },
            );
          }}
          className="flex-1 bg-success hover:bg-success-hover border-success"
        >
          Send Code
        </Button>
        <Button
          type="button"
          onClick={() => {
            onCancel();
            setEmail("");
          }}
          className="flex-1 bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary hover:bg-border-subtle-light dark:hover:bg-border-subtle"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export type AuthButtonsProps = {
  showGuest?: boolean;
  buttonTextPrefix?: string;
  initialMethod?: "none" | "email" | "guest";
  onSuccess?: () => void;
  redirectUrl?: string;
};

export const AuthButtons = ({
  showGuest = true,
  buttonTextPrefix = "Log in",
  initialMethod = "none",
  onSuccess,
  redirectUrl,
}: AuthButtonsProps) => {
  const [loginMethod, setLoginMethod] = useState<"none" | "email" | "guest">(
    initialMethod,
  );
  const [sentEmail, setSentEmail] = useState("");
  const { route } = useLocation();
  const googleUrl = auth.createAuthorizationURL({
    clientName: "google-web",
    redirectURL: redirectUrl ?? globalThis.location.href,
  });

  const handleGuestLogin = () => {
    toast.promise(
      auth.signInAsGuest().then(
        (res: { user?: { refresh_token?: string } }) => {
          if (onSuccess) {
            onSuccess();
            return;
          }
          const params = new URLSearchParams(globalThis.location?.search);
          const redirectUrl = resolveRedirectUrl(
            params.get("redirect_to"),
            res?.user?.refresh_token,
          );
          if (redirectUrl) {
            globalThis.location.href = redirectUrl;
          } else {
            route(builderChatPath);
          }
        },
      ),
      {
        loading: "Signing in as guest...",
        success: "Logged in as guest!",
        error: (err: { body?: { message: string } }) =>
          `Error: ${err.body?.message || "Failed to sign in as guest"}`,
      },
    );
  };

  return (
    <>
      <div className="flex flex-col gap-3 w-full">
        {loginMethod === "none" && (
          <>
            {showGuest && (
              <div className="flex flex-col gap-1">
                <Button
                  className={`${authButtonBase} bg-[#7c5cbf] hover:bg-[#6b4aae] text-white`}
                  onClick={() => setLoginMethod("guest")}
                  type="button"
                >
                  <span>⚡</span>
                  Quick Start - {buttonTextPrefix} as Guest
                </Button>
                <div className="text-xs text-text-tertiary-light dark:text-text-tertiary text-center">
                  No email required
                </div>
              </div>
            )}
            <Button
              className={`${authButtonBase} bg-[#c44040] hover:bg-[#b33636] text-white`}
              onClick={() => {
                globalThis.location.href = googleUrl;
              }}
            >
              <span>🔍</span>
              {buttonTextPrefix} with Google
            </Button>
            <div className="flex flex-col gap-1">
              <Button
                className={`${authButtonBase} bg-success hover:bg-success-hover text-surface`}
                onClick={() => setLoginMethod("email")}
                type="button"
              >
                <span>📧</span>
                {buttonTextPrefix} with Email
              </Button>
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary text-center">
                If you don't have Gmail
              </div>
            </div>
          </>
        )}
      </div>
      <div className="w-full">
        {loginMethod === "email" && (
          sentEmail
            ? (
              <MagicCode
                sentEmail={sentEmail}
                onSuccess={onSuccess}
                onCancel={() => {
                  setSentEmail("");
                }}
              />
            )
            : (
              <LoginWithEmail
                setSentEmail={setSentEmail}
                onCancel={() => {
                  setLoginMethod("none");
                  setSentEmail("");
                }}
              />
            )
        )}
        {loginMethod === "guest" && showGuest && (
          <GuestLoginWarning
            onAccept={handleGuestLogin}
            onCancel={() => setLoginMethod("none")}
          />
        )}
      </div>
    </>
  );
};

const GuestLoginWarning = ({ onAccept, onCancel }: {
  onAccept: () => void;
  onCancel: () => void;
}) => (
  <div className="text-left">
    <h3 className="text-lg font-medium mb-3 text-text-primary-light dark:text-text-primary font-display">
      Guest Mode
    </h3>
    <div className="text-sm text-text-secondary-light dark:text-text-secondary mb-4">
      <ul className="list-disc list-inside space-y-1.5">
        <li>You get free messages to try in your first 7 days</li>
        <li>
          Verify your mobile phone to unlock 500 free runs during your trial
        </li>
        <li>Everything will be saved while you use this browser</li>
        <li>
          If you change browsers or clear data, your information might be gone
        </li>
        <li>You can convert your guest account to an email account later</li>
      </ul>
    </div>
    <div className="flex gap-2">
      <Button
        onClick={onAccept}
        type="button"
        className="flex-1"
      >
        Continue as guest
      </Button>
      <Button
        onClick={onCancel}
        type="button"
        className="flex-1 bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary border border-border-default-light dark:border-border-default hover:bg-border-subtle-light dark:hover:bg-border-subtle"
      >
        Cancel
      </Button>
    </div>
  </div>
);

const authCardStyle =
  "w-full max-w-md bg-surface-raised-light dark:bg-surface-raised rounded-xl border border-border-subtle-light dark:border-border-subtle shadow-lg";

const LoginForm = () => {
  const { url } = useLocation();
  const params = new URL(url, globalThis.location.origin).searchParams;
  const guestRequested = params.get("guest") === "1";
  const isApprovalRedirect = params.get("redirect_to")?.includes("approve-");
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          padding: 25,
          justifyContent: "center",
          alignItems: "stretch",
          flexDirection: "column",
          gap: 10,
        }}
        className={authCardStyle}
      >
        <Logo className="text-4xl md:text-5xl mb-2" clickable={false} />
        {isApprovalRedirect && (
          <p className="text-xs text-text-secondary-light dark:text-text-secondary text-center mb-1">
            Please sign in with an authorized administrator account to continue.
          </p>
        )}
        <AuthButtons
          showGuest={!isApprovalRedirect}
          initialMethod={guestRequested && !isApprovalRedirect
            ? "guest"
            : "none"}
        />
      </div>
    </div>
  );
};

export const WithLogin = (Component: ComponentType<VoidProps>) => () => {
  const { isLoading, user, error } = useAuth();
  if (isLoading) return <PageSpinner />;
  if (error) {
    return <div className="text-danger">Uh oh! {error.message}</div>;
  }
  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      {!user ? <LoginForm /> : <Component />}
    </div>
  );
};

export { loginPath } from "../../constants.ts";

export const LoginCta = (
  { text, className }: { text: string; className?: string },
) => {
  const route = useLocation().route;
  const signedIn = !!useAuth().user;
  return (
    <CtaButton
      onClick={() => {
        fireAnalyticsEventLegacy("signin_cta_click");
        posthog.capture("Signin CTA Click");
        if (signedIn) route(builderChatPath);
        else route(loginPath);
      }}
      text={text}
      className={className}
    />
  );
};

const UpgradeForm = () => {
  const { route } = useLocation();
  const { user, isLoading } = useAuth();

  const handleRedirect = () => {
    const params = new URLSearchParams(globalThis.location?.search);
    const redirectUrl = resolveRedirectUrl(
      params.get("redirect_to"),
      user?.refresh_token,
    );
    if (redirectUrl) {
      globalThis.location.href = redirectUrl;
      return;
    }
    route(builderChatPath);
  };

  useEffect(() => {
    if (user?.email) {
      handleRedirect();
    }
  }, [user?.email]);

  if (isLoading) return <PageSpinner />;

  if (!user) {
    const params = new URLSearchParams(globalThis.location?.search);
    const redirectTo = params.get("redirect_to");
    route(
      redirectTo
        ? `${loginPath}?redirect_to=${encodeURIComponent(redirectTo)}`
        : loginPath,
    );
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          padding: 25,
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 10,
        }}
        className={authCardStyle}
      >
        <Logo className="text-4xl md:text-5xl mb-2" clickable={false} />
        <h2 className="text-xl font-display font-semibold mb-4 text-text-primary-light dark:text-text-primary">
          Upgrade Your Account
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-center mb-4">
          Convert your guest account to a permanent account with email login
        </p>
        <AuthButtons
          showGuest={false}
          buttonTextPrefix="Upgrade"
          onSuccess={handleRedirect}
        />
        <Button
          className="mt-4 text-sm text-text-tertiary-light dark:text-text-tertiary bg-transparent hover:bg-surface-overlay-light dark:hover:bg-surface-overlay"
          onClick={handleRedirect}
          type="button"
        >
          Continue as guest
        </Button>
      </div>
    </div>
  );
};

export const LoginPage = () => {
  usePageMeta({
    title: "Log in | prompt2bot",
    description: "Log in to prompt2bot to build and manage your AI agents.",
  });
  const { user } = useAuth();
  const signedIn = !!user;
  const route = useLocation().route;
  const [switchingAccount, setSwitchingAccount] = useState(false);

  useEffect(() => {
    if (typeof globalThis.location === "undefined") return;
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get(switchAccountParam) === "true") {
      setSwitchingAccount(true);
      const url = new URL(globalThis.location.href);
      url.searchParams.delete(switchAccountParam);
      globalThis.history.replaceState({}, "", url.pathname + url.search);
      Promise.resolve(auth.signOut?.()).catch(() => {}).finally(() => {
        setSwitchingAccount(false);
      });
      return;
    }
    if (switchingAccount) return;
    if (signedIn) {
      const redirectUrl = resolveRedirectUrl(
        params.get("redirect_to"),
        user?.refresh_token,
      );
      if (redirectUrl) {
        globalThis.location.href = redirectUrl;
      } else {
        route(builderChatPath);
      }
    }
  }, [signedIn, user, switchingAccount]);

  return <LoginForm />;
};

export const UpgradePage = () => {
  usePageMeta({
    title: "Upgrade your account | prompt2bot",
    description:
      "Convert your guest account to a permanent prompt2bot account.",
  });
  return <UpgradeForm />;
};
